import {
  Box,
  Dialog,
  DialogContent,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { Crown, Globe, LayoutGrid, Palette } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SPACING } from "../../constants/spacing";
import { useTheme } from "../../contexts/ThemeContext";
import LanguageSettings from "./LanguageSettings";
import SubscriptionSettings from "./SubscriptionSettings";
import ThemeSettings from "./ThemeSettings";
import ViewSettings from "./ViewSettings";

function SettingsModal({ showSettingsModal, setShowSettingsModal }) {
  const { t } = useTranslation("settings");
  const [selectedTab, setSelectedTab] = useState("theme");
  const { colors } = useTheme();

  const handleClose = () => setShowSettingsModal(false);

  const settingsTabs = [
    {
      id: "theme",
      label: t("tabs.appearance"),
      icon: Palette,
      color: "inherit",
    },
    {
      id: "subscription",
      label: t("tabs.subscription"),
      icon: Crown,
      color: "inherit",
    },
    {
      id: "language",
      label: t("tabs.language"),
      icon: Globe,
      color: "inherit",
    },
    {
      id: "view",
      label: t("tabs.view"),
      icon: LayoutGrid,
      color: "inherit",
    },
  ];

  const renderContent = () => {
    switch (selectedTab) {
      case "theme":
        return <ThemeSettings />;
      case "language":
        return <LanguageSettings />;
      case "view":
        return <ViewSettings />;
      case "subscription":
        return <SubscriptionSettings />;
      default:
        return <ThemeSettings />;
    }
  };

  return (
    <Dialog
      open={showSettingsModal}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: "60vh",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogContent
        sx={{ p: 0, display: "flex", height: "100%", position: "relative" }}
      >
        <Box
          sx={{
            width: { xs: 80, sm: 250 },
            borderRight: "1px solid",
            borderColor: colors.glass?.border || "divider",
            display: "flex",
            backgroundColor: colors.glass?.background || colors.pageSecondary,
            backdropFilter: colors.glass?.blur
              ? `blur(${colors.glass.blur}) saturate(180%)`
              : "none",
            backgroundImage: `linear-gradient(180deg, rgba(59, 130, 246, 0.08) 0%, rgba(59, 130, 246, 0.03) 40%, rgba(139, 92, 246, 0.02) 100%)`,
            flexDirection: "column",
          }}
        >
          {/* Navigation List */}
          <List sx={{ px: 2, py: 2, flex: 1 }}>
            {settingsTabs.map((tab) => {
              const IconComponent = tab.icon;
              const isSelected = selectedTab === tab.id;

              return (
                <ListItem key={tab.id} disablePadding sx={{ mb: SPACING.xs }}>
                  <ListItemButton
                    selected={isSelected}
                    onClick={() => setSelectedTab(tab.id)}
                    sx={{
                      borderRadius: 2,
                      py: 1.5,
                      "&.Mui-selected": {
                        backgroundColor: "action.selected",
                        "&:hover": {
                          backgroundColor: "action.selected",
                        },
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: { xs: "auto", sm: 40 },
                        justifyContent: { xs: "center", sm: "flex-start" },
                      }}
                    >
                      <IconComponent size={18} />
                    </ListItemIcon>
                    <ListItemText
                      primary={tab.label}
                      primaryTypographyProps={{
                        variant: "bodySm",
                      }}
                      sx={{
                        display: { xs: "none", sm: "block" },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            backgroundColor: "background.paper",
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              p: 4,
            }}
          >
            {renderContent()}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default SettingsModal;
